import cn from 'classnames';
import { render } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import Button, { SecondaryButton, TertiaryButton } from '../button';
import Form from '../form';
import FormField from '../form-field';
import { MenuList } from '../list';
import { generateId } from '../../utility';
import { useMergedRef } from '../../hooks/useMergedRef';
import type { ComponentChildren, ComponentProps, VNode, TargetedEvent } from 'preact';
import type { IconType } from '../../types/icons';
import type { HTMLAttributes, PickOptional, RequireAtLeastOne } from '../../types';

const ALERT_VALUES = ['alert', 'confirm', 'prompt'] as const;
const MODAL_VALUES = ['modal', 'modal-drawer'] as const;
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- the typing pattern matches other used vars, so disabling for readability
const NON_MODAL_VALUES = ['non-modal', 'notification', 'drawer'] as const;

const ALERT_TYPES = new Set<string>(ALERT_VALUES);
const MODAL_TYPES = new Set<string>([...MODAL_VALUES, ...ALERT_VALUES]);
const CLOSABLE_TYPES = new Set<string>([...MODAL_VALUES, 'drawer']);

type ButtonProps = ComponentProps<typeof Button>;

interface DialogProps {
  autoDismiss?: boolean | number;
  cancelLabel?: string;
  children: ComponentChildren;
  confirmMessage?: ComponentProps<typeof Form>['confirmMessage'];
  disableCloseOnSubmit?: boolean;
  errorMessage?: string | ComponentProps<typeof Form>['errorMessage'];
  footerChildren?: ComponentChildren;
  position?: 'top-right' | 'bottom-right' | 'bottom-left' | 'top-left'; // TODO: only valid for `{ type: 'non-modal' }`; having TS issues conditionally applying to Props
  size?: 'small' | 'medium';
  subheading?: ComponentChildren;
  submitLabel?: string;
  successMessage?: ComponentProps<typeof Form>['successMessage'];
  type?:
    | (typeof ALERT_VALUES)[number]
    | (typeof MODAL_VALUES)[number]
    | (typeof NON_MODAL_VALUES)[number];
  variants?: Variants;
  onDismiss: (e?: TargetedEvent<HTMLDialogElement>) => void;
  onSubmit?: ComponentProps<typeof Form>['onSubmit'];
  onReset?: (e: TargetedEvent<HTMLFormElement>) => void;
}

type HeadingProps = RequireAtLeastOne<{
  'aria-label': string;
  heading: string;
}>;

type Props = HeadingProps &
  DialogProps &
  Omit<HTMLAttributes<HTMLDialogElement>, 'action' | 'onSubmit'>;

type PropsWithoutType = Omit<Props, 'type'>;

type AlertProps = PropsWithoutType;

type ActionButtonProps = { children?: string; value?: string; form?: string } & Pick<
  ButtonProps,
  'hierarchy'
>;

type NotificationProps = Omit<PropsWithoutType, 'heading' | 'subheading'> & {
  icon?: IconType;
  action?:
    | VNode<ButtonProps>
    | { label: string; onClick: (e: TargetedEvent<HTMLButtonElement>) => void };
  theme?: 'default' | 'positive' | 'negative';
};

function handleCancelClick(e: TargetedEvent<HTMLButtonElement | HTMLDialogElement>) {
  const { currentTarget } = e;
  const dialog = currentTarget.closest('dialog');
  dialog?.close(currentTarget instanceof HTMLButtonElement ? currentTarget.value : undefined);
  tryForceRemove(dialog);
}

// NOTE: this is only intended when a dialog is rendered outside the <App /> context since there's no state to re-render and remove the node
function tryForceRemove(dialog: HTMLDialogElement | null) {
  if (!dialog || !dialog.dataset.forceRemove) {
    return;
  }

  window.requestAnimationFrame(() => {
    dialog.remove();
  });
}

export const DEFAULT_AUTO_DISMISS = 5000;

// a bitmask enum for boolean props
export enum Variants {
  None = 0,
  Fullscreen = 1,
  HasChildrenWithForm = 2,
  Stepped = 4,
  // future "prop" e.g. Resizable = 8
}

export default function Dialog({
  'aria-label': ariaLabel,
  autoDismiss,
  cancelLabel,
  children,
  className,
  confirmMessage,
  disableCloseOnSubmit = false,
  errorMessage,
  footerChildren,
  heading,
  open = false,
  position = 'top-right',
  // React 19 delivers `ref` as a regular prop; pull it out so the spread
  // below can't clobber our internal ref (which showModal/show depend on)
  ref: forwardedRef,
  size = 'medium',
  subheading,
  submitLabel,
  type = 'modal',
  variants = Variants.None,
  onDismiss,
  onSubmit,
  onReset,
  ...props
}: Props) {
  const isModal = MODAL_TYPES.has(type);
  const isAlert = ALERT_TYPES.has(type);
  const autodismissTimeout = autoDismiss === true ? DEFAULT_AUTO_DISMISS : autoDismiss;
  const hasSubmitButton = !!(submitLabel || onSubmit);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const mergedDialogRef = useMergedRef(dialogRef, forwardedRef);
  const { formId, headingId } = useMemo(() => {
    const id = generateId();
    return { formId: `form-${id}`, headingId: `heading-${id}` };
  }, []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { isFullscreen, hasChildrenWithForm, isStepped } = useMemo(
    () => ({
      isFullscreen: !!(variants & Variants.Fullscreen),
      hasChildrenWithForm: !!(variants & Variants.HasChildrenWithForm),
      isStepped: !!(variants & Variants.Stepped),
    }),
    [variants],
  );

  const handleSubmit = useCallback(
    async (e: TargetedEvent<HTMLFormElement, SubmitEvent>) => {
      const form = e.target as HTMLFormElement; // not using currentTarget because <Form> onSubmit is async and currentTarget may be null
      const dialog = form.closest('dialog');

      if (dialog && form.field) {
        dialog.returnValue = form.field.value;
        (e.submitter as HTMLButtonElement).value = form.field.value;
      }

      setIsSubmitting(true);
      setError('');

      let ret;
      try {
        ret = await onSubmit?.(e);

        if (ret?.error) {
          setError(ret.error?.toString());
        } else if (!disableCloseOnSubmit) {
          dialog?.close((e.submitter as HTMLButtonElement)?.value);
          tryForceRemove(dialog);
        }
      } catch (ex) {
        console.error(ex);
        setError(ex instanceof Error ? ex.message : ex?.toString() || 'An unknown error occurred');
      } finally {
        setIsSubmitting(false);
      }

      return ret;
    },
    [disableCloseOnSubmit, onSubmit],
  );

  // native event listeners
  useEffect(() => {
    const { current } = dialogRef;

    if (current && open) {
      if (isModal) {
        current.showModal();
      } else {
        current.show();
      }
      current.addEventListener('close', onDismiss as EventListener);
      current.addEventListener('cancel', onDismiss as EventListener);
    }

    if (!open) {
      setError('');
    }

    return () => {
      if (current) {
        current.removeEventListener('close', onDismiss as EventListener);
        current.removeEventListener('cancel', onDismiss as EventListener);
      }
    };
  }, [isModal, open, onDismiss]);

  // auto-dismiss
  useEffect(() => {
    const { current } = dialogRef;
    let timeout: number;

    if (autodismissTimeout && current && open) {
      timeout = window.setTimeout(() => {
        current.close();
        tryForceRemove(current);
      }, autodismissTimeout);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [open, autodismissTimeout]);

  if (!open) {
    return null;
  }

  const isNotification = type === 'notification';
  const isNotAlertOrNotification = !isAlert && !isNotification;
  const shouldRenderHeading = type === 'modal' || (!isNotification && heading);

  const classList = cn(className, {
    ['allow-fullscreen']: isFullscreen,
    [`position-${position}`]: type === 'non-modal' && position !== 'top-right',
  });

  return (
    <dialog
      ref={mergedDialogRef}
      className={classList || undefined}
      role={isAlert ? 'alertdialog' : undefined}
      data-type={type}
      data-size={size}
      aria-label={ariaLabel}
      aria-labelledby={heading ? headingId : undefined}
      {...props}
    >
      {shouldRenderHeading && (
        <header>
          {heading && <h1 id={headingId}>{heading}</h1>}
          {subheading && <div className="subheading">{subheading}</div>}

          {CLOSABLE_TYPES.has(type) && (
            <>
              {isStepped ? <BackButton form={formId} /> : null}
              <SecondaryButton
                aria-label="Close"
                form={formId}
                icon="close"
                isIconOnly
                size="small"
                name="act"
                value="close"
                onClick={handleCancelClick}
              />
            </>
          )}
        </header>
      )}

      <Form
        confirmMessage={confirmMessage}
        errorMessage={typeof errorMessage === 'string' ? undefined : errorMessage}
        successMessage={props.successMessage}
        method="dialog"
        id={formId}
        onSubmit={handleSubmit}
        onReset={onReset}
        className={hasChildrenWithForm ? 'has-separate-form' : undefined}
      >
        {!hasChildrenWithForm && children}
      </Form>

      {hasChildrenWithForm && children}

      {isNotAlertOrNotification && (
        <footer className={cn({ 'has-overridden-footer': footerChildren })}>
          {footerChildren}

          {!footerChildren && (
            <>
              {/*
                NOTE: this error message pattern _should_ be deprecated;
                we should use <Form errorMessage={{ component: 'Banner|Notification' }} /> instead;
                keeping for backward compatibility for now
              */}
              {error && typeof errorMessage === 'string' && (
                <p className="submission-error" role="alert">
                  {errorMessage || error}
                </p>
              )}

              <MenuList
                items={[
                  <CancelButton key="cancel" form={formId}>
                    {cancelLabel ?? (hasSubmitButton ? 'Cancel' : 'Dismiss')}
                  </CancelButton>,
                  hasSubmitButton ? (
                    <OkayButton key="ok" form={formId} isLoading={isSubmitting}>
                      {submitLabel ?? 'Save'}
                    </OkayButton>
                  ) : null,
                ].filter(Boolean)}
              />
            </>
          )}
        </footer>
      )}
    </dialog>
  );
}

Dialog.Variants = Variants;

interface UseDialogProps {
  isOpen?: boolean;
  onOpen?: (e?: TargetedEvent) => void;
  onDismiss?: DialogProps['onDismiss'];
}

Dialog.useDialog = function useDialog(props?: UseDialogProps) {
  const { isOpen: passedIsOpen, onOpen, onDismiss } = props ?? {};
  const [isOpen, setIsOpen] = useState(!!passedIsOpen);

  const handleOpen = useCallback(
    (e?: TargetedEvent) => {
      setIsOpen(true);
      onOpen?.(e);
    },
    [onOpen],
  );

  const handleDismiss = useCallback(
    (e?: TargetedEvent<HTMLDialogElement>) => {
      setIsOpen(false);
      onDismiss?.(e);
    },
    [onDismiss],
  );

  return { isOpen, setIsOpen, handleOpen, handleDismiss };
};

function handleDialogClick(e: TargetedEvent<HTMLDialogElement, MouseEvent>) {
  const { clientX, currentTarget, target } = e;
  if (target === currentTarget) {
    const { left } = currentTarget.getBoundingClientRect();

    // ::backdrop was clicked
    if (clientX < left) {
      currentTarget.close();
    }
  }
}

export function Drawer({ isModal, ...props }: Props & { isModal?: boolean }) {
  return (
    <Dialog
      {...props}
      type={isModal ? 'modal-drawer' : 'drawer'}
      onClick={isModal ? handleDialogClick : undefined}
    />
  );
}

export function Notification({
  children,
  className,
  icon,
  action,
  autoDismiss,
  theme = 'default',
  ...props
}: NotificationProps) {
  const items = useMemo(() => {
    const items = [
      <TertiaryButton
        type="submit"
        name="act"
        value="close"
        key="close"
        aria-label="Close"
        icon="close"
        isIconOnly
        size="small"
      />,
    ];

    if (action) {
      if ('label' in action) {
        items.unshift(
          <TertiaryButton size="small" onClick={action.onClick}>
            {action.label}
          </TertiaryButton>,
        );
      } else {
        items.unshift(action as VNode<ButtonProps>);
      }
    }

    return items;
  }, [action]);

  const classList =
    cn(className, {
      'success-notification': theme === 'positive',
      'error-notification': theme === 'negative',
    }) || undefined;

  if (theme === 'positive') {
    icon = 'check';
  } else if (theme === 'negative') {
    icon = 'close';
  }

  return (
    <Dialog
      aria-label="Notification"
      className={classList}
      {...props}
      data-icon={icon}
      type="notification"
      autoDismiss={autoDismiss === false ? false : autoDismiss || !action}
    >
      {children}
      <MenuList aria-label="notification actions" items={items} />
    </Dialog>
  );
}

type ImperativeAlertProps = PickOptional<AlertProps, 'onDismiss'>;
type ImperativeNotficationProps = PickOptional<NotificationProps, 'onDismiss'>;
type ImperativeDialogProps = ImperativeAlertProps | ImperativeNotficationProps;
type ComponentType =
  | typeof Notification
  | typeof SuccessNotification
  | typeof ErrorNotification
  | typeof Alert
  | typeof Confirm
  | typeof Prompt;

function show(props: ImperativeDialogProps, Component: ComponentType) {
  return render(
    <Component
      open={true}
      onDismiss={handleCancelClick as DialogProps['onDismiss']}
      {...props}
      key={Math.random()}
      data-force-remove={true}
    />,
    getDialogContainer(),
  );
}

Notification.show = function showNotification(props: ImperativeNotficationProps) {
  show(props, Notification);
};

export function SuccessNotification(props: NotificationProps) {
  return <Notification {...props} theme="positive" />;
}

SuccessNotification.show = function showSuccessNotification(props: ImperativeNotficationProps) {
  show(props, SuccessNotification);
};

export function ErrorNotification(props: NotificationProps) {
  return <Notification {...props} theme="negative" />;
}

ErrorNotification.show = function showErrorNotification(props: ImperativeNotficationProps) {
  show(props, ErrorNotification);
};

export function Alert({ children, submitLabel, ...props }: AlertProps) {
  return (
    <Dialog heading="Alert" type="alert" {...props}>
      {children}
      <MenuList
        aria-label="Actions"
        items={[
          <OkayButton key="ok" value="true">
            {submitLabel}
          </OkayButton>,
        ]}
      />
    </Dialog>
  );
}

Alert.show = function showAlert(props: ImperativeAlertProps) {
  show(props, Alert);
};

export function Confirm({ children, submitLabel, cancelLabel, ...props }: AlertProps) {
  return (
    <Dialog heading="Confirm" type="confirm" {...props}>
      {children}
      <MenuList
        aria-label="Actions"
        items={[
          <CancelButton key="cancel" value="false">
            {cancelLabel}
          </CancelButton>,
          <OkayButton key="ok" value="true">
            {submitLabel}
          </OkayButton>,
        ]}
      />
    </Dialog>
  );
}

const DIALOG_DISMISS_EVENTS = ['cancel', 'close'];

Confirm.show = async function showConfirm(props: ImperativeAlertProps) {
  show(props, Confirm);

  const returnValue = await new Promise<boolean>((resolve) => {
    const handler = (e: Event) => {
      const confirm = e.target as HTMLDialogElement;
      const isTrue = confirm.returnValue === 'true';

      DIALOG_DISMISS_EVENTS.forEach((name) => document.removeEventListener(name, handler, true));

      resolve(isTrue);
    };

    // attach a capture event listening for the dialog close
    DIALOG_DISMISS_EVENTS.forEach((name) => document.addEventListener(name, handler, true));
  });

  return returnValue;
};

export function Prompt({
  children,
  defaultValue,
  ...props
}: AlertProps & { defaultValue?: string | number | boolean }) {
  return (
    <Dialog heading="Prompt" type="prompt" {...props}>
      {children}
      <FormField
        aria-label="Enter a value"
        name="field"
        autoComplete="off"
        defaultValue={defaultValue as string}
      />
      <MenuList
        aria-label="Actions"
        items={[<CancelButton key="cancel" value="" />, <OkayButton key="ok" />]}
      />
    </Dialog>
  );
}

Prompt.show = async function showPrompt(props: ImperativeAlertProps) {
  show(props, Prompt);

  const returnValue = await new Promise((resolve) => {
    const handler = (e: Event) => {
      const prompt = e.target as HTMLDialogElement;

      DIALOG_DISMISS_EVENTS.forEach((name) => document.removeEventListener(name, handler, true));

      resolve(prompt.returnValue);
    };

    // attach a capture event listening for the dialog close
    DIALOG_DISMISS_EVENTS.forEach((name) => document.addEventListener(name, handler, true));
  });

  return returnValue;
};

Drawer.useDrawer = Dialog.useDialog;
Notification.useNotification = Dialog.useDialog;
SuccessNotification.useSuccessNotification = Dialog.useDialog;
ErrorNotification.useErrorNotification = Dialog.useDialog;
Alert.useAlert = Dialog.useDialog;
Confirm.useConfirm = Dialog.useDialog;
Prompt.usePrompt = Dialog.useDialog;

function CancelButton({ children, ...props }: ActionButtonProps) {
  return (
    <Button value="cancel" name="act" hierarchy="tertiary" onClick={handleCancelClick} {...props}>
      {children || 'Cancel'}
    </Button>
  );
}

function OkayButton({ children, ...props }: ActionButtonProps & { isLoading?: boolean }) {
  return (
    <Button type="submit" name="act" {...props}>
      {children || 'Ok'}
    </Button>
  );
}

function BackButton(props: Omit<ActionButtonProps, 'children'>) {
  return (
    <SecondaryButton
      aria-label="Back"
      className="dialog-back-button"
      icon="chevron-left"
      name="act"
      type="reset"
      value="back"
      isIconOnly
      {...props}
    />
  );
}

const DIALOG_CONTAINER_ID = 'dialog-container';

function getDialogContainer() {
  const placeholder = document.getElementById(DIALOG_CONTAINER_ID) || document.createElement('div');

  if (!document.body.contains(placeholder)) {
    placeholder.id = DIALOG_CONTAINER_ID;
    document.body.appendChild(placeholder);
  }

  return placeholder;
}
